import * as Sentry from "@sentry/react";

const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

const getPrice = (variant) => {
    return variant.specialPriceRec && variant.specialPriceRec < variant.priceRec
        ? roundToTwoDecimals(variant.specialPriceRec)
        : roundToTwoDecimals(variant.priceRec);
};

function deconstructAddress(addressString) {
    // Regular expression to match different parts of the address
    const addressRegex = /^(.*?),\s*(.*?),\s*(\w{2})\s*(\d{5}),\s*(\w+)$/;
    const match = addressString.match(addressRegex);

    if (match) {
        const [, street, city, state, zip, country] = match;

        return {
            street,
            city,
            state,
            zip,
            country: country === 'USA' ? 'US' : country
        };
    } else {
        console.error('Unable to parse address string');
        return null;
    }
}

export function didSurfsideLoad(callback) {
    if (window.isCheckingSurfside) return;

    window.isCheckingSurfside = true;
    const intervalId = setInterval(() => {
        if (surf) {
            try {
                callback();
            } catch (error) {
                console.error("Error in Surfside callback:", error);
                Sentry.captureException(error);
            }
            clearInterval(intervalId);
            window.isCheckingSurfside = false;
        }
    }, 200);
}

export function trackSurfsideEvent(type, product, selectedVariant, quantity = null, specialData = null, retailerId, retailerData) {
    if (!surf) {
        console.error('Surfside SDK is not initialized.');
        return;
    }
      
    // Add Location Context
    let addressComponents;
    if (retailerData && retailerId && type == 'add') {    
        addressComponents = deconstructAddress(retailerData.address);
        
        const currentLocation = {
            id: retailerId,
            street: addressComponents?.street,
            city: addressComponents?.city,
            state_label: addressComponents?.state,
            zip: addressComponents?.zip,
            country_code: addressComponents?.country,
        };
        
        surf('setLocation', currentLocation);
    }
    
    // Add Product Data
    const price = getPrice(selectedVariant);
   
    const surfsideData = {
        id: product.id,
        name: product.name,
        brand: product.brand?.name,
        category: product.category,
        category2: product.subcategory,
        variant: selectedVariant.option,
        price: price,
        quantity: quantity || 1,
        discount: roundToTwoDecimals(selectedVariant.priceRec - price),
        currency: 'USD'
    };

    if (specialData) {
        surfsideData.coupon = specialData;
    }
    
    surf('addProduct:range', surfsideData);
    
    switch (type) {
        case 'add':
            surf('setCommerceAction:range', 'add');
            break;
        case 'remove':
            surf('setCommerceAction:range', 'remove');
            break;
        case 'detail':
            surf('setCommerceAction:range', 'detail');
            break;
        default:
            return;
    }

    Sentry.captureEvent({
        message: `Product ${type === 'add' ? 'added to' : type === 'remove' ? 'removed from' : 'viewed in'} cart`,
        level: 'info',
        tags: surfsideData
    });
}

export function trackSurfsideProductsLoop(checkoutData, type, specialsData) {
    if (!surf) {
        console.error('Surfside SDK is not initialized.');
        return;
    }
    
    let products;
    
    if (!checkoutData.items) {
        console.error('No products to send to Surfside checkout');
        return;
    } else {
        products = checkoutData.items;
    }

    products.forEach(product => {
        const productData = product.product;
        const selectedVariant = productData.variants.find(variant => variant.option === product.option);
        const specialData = specialsData?.[productData.id];

        trackSurfsideEvent(type, productData, selectedVariant, product.quantity, specialData);
    });
    
    surf('setCommerceAction:range', 'checkout');
}

export function trackSurfsidePurchase(orderNumber, order, retailerId, retailerData) {
    if (!surf) {
        console.error('Surfside SDK is not initialized.');
        return;
    }

    // Add Location Context
    let addressComponents;
    if (retailerData && retailerId) {    
        addressComponents = deconstructAddress(retailerData.address);
        
        const currentLocation = {
            id: retailerId,
            street: addressComponents?.street,
            city: addressComponents?.city,
            state_label: addressComponents?.state,
            zip: addressComponents?.zip,
            country_code: addressComponents?.country,
        };
        
        surf('setLocation', currentLocation);
    }
    
    const checkoutData = {
        id: orderNumber,
        revenue: order.total,
        tax: order.tax || 0,
        shipping: 0,
        currency: 'USD'
    };
    
    surf('addTransaction:range', checkoutData);

    Sentry.captureEvent({
        message: 'Successful checkout transaction',
        level: 'info',
        tags: { ...checkoutData, customer_id: order?.customer.id, order_type: order.pickup ? "pickup" : "delivery", payment_type: order.paymentMethod }
    });

    let orderProducts;
    
    if (!order.items) {
        return;
    } else {
        orderProducts = order.items;
    }
    
    orderProducts.forEach(product => {
        // let specialData;
        const productData = product.product;
        const selectedVariant = productData.variants.find(variant => variant.option === product.option);
        // specialData = specialsData?.[productData.id];

        trackSurfsideEvent(null, productData, selectedVariant, product.quantity);
    });

    surf('setCommerceAction:range', 'purchase');
}
